import { Button, Grid, Tooltip, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import {
  FormField2,
  getValidationSchema,
  IDynamicFormValue,
  IMultiSelectControlField2,
  ISelectOption
} from 'src/lib';
import '../../../../../../components/filters/components/filters.scss';
import {
  DialogResult,
  DynamicFormFieldRenderer,
  AlertDialog
} from 'src/components/analytics';
import { getNextValues, ICombinationModel } from 'src/components/filters';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import * as Yup from 'yup';
import { AnyObject } from 'yup/lib/types';
import { useAppSelector } from 'src/hooks';
import { ConfigDiffFinder } from '../../utils';

interface IFilterProps {
  initialValues: IDynamicFormValue;
  formFields: FormField2[];
  combinations: ICombinationModel[];
  // eslint-disable-next-line react/require-default-props
  isApplyDisabled?: boolean;
  // eslint-disable-next-line react/require-default-props
  isResetDisabled?: boolean;
  // eslint-disable-next-line react/require-default-props
  externalReset?: boolean;
  formValueInState: IDynamicFormValue;
  onChange: (arg: ReturnType<typeof getNextValues>) => void;
  onReset: (newValues: IDynamicFormValue) => void;
  onApplyFilters: (values: IDynamicFormValue) => void;
}

export default function Filters2({
  formFields,
  initialValues,
  isResetDisabled = false,
  isApplyDisabled = false,
  externalReset = false,
  formValueInState,
  onReset,
  onChange,
  onApplyFilters,
  combinations
}: IFilterProps) {
  const [schema, setSchema] = useState(Yup.object().shape({}));

  const form = useFormik<IDynamicFormValue>({
    initialValues,
    enableReinitialize: true,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onSubmit: () => {},
    validationSchema: schema
  });

  const { cardsSequence: cards } = useAppSelector(
    (state) => state.simulatorHome
  );

  const [resetFilterMessage, setResetFilterMessage] = useState(
    'Filters will reset and all changes to simulation will be lost. Continue?'
  );

  const setResetMessage = () => {
    const currentUrl = window.location.href;
    let isElementPreset = false;
    for (let i = 0; i < cards.length; i++) {
      if (currentUrl.toLowerCase().includes(cards[i].title.toLowerCase())) {
        isElementPreset = true;
        break;
      }
    }
    if (!isElementPreset) {
      setResetFilterMessage(
        'Filters will reset and all changes will be lost. Continue?'
      );
    }
  };

  const [isValueChanged, setIsValueChanged] = useState(false);

  const [isConfirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const handleConfirmationDialogClose = (result: DialogResult) => {
    if (result === DialogResult.YES) {
      form.setValues(initialValues);
      setIsValueChanged(false);
      onReset(initialValues);
    }
    setConfirmationDialogOpen(false);
  };

  const onResetClick = () => {
    setConfirmationDialogOpen(true);
  };

  const handleFieldValueChange = (value: unknown, field: FormField2): void => {
    // checking if selected fields crosses the limit of allowed selection
    if ((field as IMultiSelectControlField2).limit) {
      if (
        (value as ISelectOption[]).length >
        (field as IMultiSelectControlField2).limit
      ) {
        return;
      }
    }

    const { newFormValues, fieldNameToOptionsMap } = getNextValues(
      field.name,
      value as ISelectOption[],
      formFields,
      combinations,
      form.values
    );
    form.setValues(newFormValues);
    setIsValueChanged(
      true
    );
    onChange({ newFormValues, fieldNameToOptionsMap });
  };

  useEffect(() => {
    if (externalReset) {
      form.setValues(initialValues);
      setIsValueChanged(false);
    }
  }, [externalReset]);

  useEffect(() => {
    setResetMessage();
  }, []);

  /**
   * A function to get validation schema for formik
   * @param fields All fields
   * @param ppgToggle The current value of toggle (new or existing ppg)
   * @returns a Yup schema validation object for each of the field
   */
  const getValidationSchemaForFields = (fields: FormField2[]) =>
    fields
      .map((field) => {
        /**
         * passing 1st arguement as field &
         * 2nd arguement as showErrorMessage true or false i.e to show the error message or not on invalid form
         */
        const fieldSchema = getValidationSchema(field, false) as {
          [key: string]: Yup.StringSchema<string, AnyObject, string>;
        };
        return fieldSchema;
      })
      .reduce(
        (prev, curr) => ({ ...prev, ...curr }),
        {} as { [key: string]: any }
      );

  useEffect(() => {
    const validationSchema = getValidationSchemaForFields(formFields);
    setSchema(Yup.object().shape(validationSchema));
  }, []);

  return (
    <>
      <form onSubmit={form.submitForm}>
        <Grid className='filter-container' container alignItems='flex-end'>
          <Grid item container spacing={1} xs justifyContent='stretch'>
            {formFields.map((formField) => (
              <Grid item xs md={12 / formFields.length} key={formField.name}>
                <Typography
                  fontSize='12px'
                  component='label'
                  display='flex'
                  justifyContent='space-between'
                >
                  <div>{formField.alias}</div>
                  {formField.is_info_enable ? (
                    <div style={{ marginRight: '2px' }}>
                      <Tooltip title={formField.info_details}>
                        <InfoOutlinedIcon
                          style={{
                            fontSize: '14px',
                            paddingTop: '0px',
                            marginLeft: '6px'
                          }}
                        />
                      </Tooltip>
                    </div>
                  ) : (
                    ''
                  )}
                </Typography>
                <DynamicFormFieldRenderer
                  field={formField}
                  form={form}
                  handleChange={(newValue) =>
                    handleFieldValueChange(newValue, formField)
                  }
                />
              </Grid>
            ))}
          </Grid>
          <Grid
            pl={1}
            pt={2}
            item
            xs='auto'
            container
            spacing={1}
            justifyContent='center'
          >
            <Grid item xs>
              <Button
                onClick={() => {
                  setIsValueChanged(false);
                  onApplyFilters(form.values);
                }}
                disabled={!form.isValid || isApplyDisabled || !isValueChanged}
                type='button'
                variant='outlined'
              >
                Apply
              </Button>
            </Grid>
            <Grid item xs>
              <Button
                onClick={onResetClick}
                type='button'
                variant='outlined'
                disabled={isResetDisabled}
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </form>
      <AlertDialog
        closeHandler={handleConfirmationDialogClose}
        isDialogOpen={isConfirmationDialogOpen}
        message={resetFilterMessage}
        title='Reset'
      />
    </>
  );
}
